import { Component, EventEmitter, Input, Output, inject, signal, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';

import { DialogComponent } from '../dialog/dialog.component';
import { ButtonComponent } from '../button/button.component';

import { DealsApiService } from '@/pages/deals/data-access/deals-api.service';
import { HelpersService } from '@/shared/services/helpers.service';
import { finalize } from 'rxjs/operators';

const QUESTION_TYPE_KEYS = [
    'ADMIN.DEALS_TAB.QUESTION_TYPE_COMMERCIAL',
    'ADMIN.DEALS_TAB.QUESTION_TYPE_FINANCIAL',
    'ADMIN.DEALS_TAB.QUESTION_TYPE_LEGAL',
    'ADMIN.DEALS_TAB.QUESTION_TYPE_OTHER'
] as const;

const QUESTION_TYPE_FALLBACKS = ['Commercial', 'Financial', 'Legal','Other'] as const;

@Component({
    selector: 'app-contact-rm-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        Dialog,
        Select,
        TextareaModule,
        DialogComponent,
        ButtonComponent
    ],
    templateUrl: './contact-rm-dialog.component.html',
    styleUrl: './contact-rm-dialog.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class ContactRmDialogComponent implements OnInit {
    @Input() set visible(value: boolean) {
        this._visible = value;
    }
    get visible(): boolean {
        return this._visible;
    }
    @Input() public dealId!: number;
    @Input() public dealName!: string;
    /** When provided, uses deal-specific Contact RM API (pending). Falls back to general contact otherwise. */
    @Input() public clientDealId?: number;
    @Output() public visibleChange = new EventEmitter<boolean>();
    @Output() public onSubmitSuccess = new EventEmitter<void>();

    // Initialize form immediately to prevent template binding errors
    public contactForm: FormGroup = inject(FormBuilder).group({
        request: [null],
        comments: ['']
    });
    public submitting = signal<boolean>(false);
    public requestTypes: { label: string; value: number }[] = [];

    private _visible: boolean = false;

    private readonly _fb = inject(FormBuilder);
    private readonly _translateService = inject(TranslateService);
    private readonly _dealsApiService = inject(DealsApiService);
    private readonly _helpersService = inject(HelpersService);

    constructor() {
        // Form initialized as property initializer
    }

    public ngOnInit(): void {
        // Update validators
        this.contactForm.get('request')?.setValidators(Validators.required);
        this.contactForm.get('comments')?.setValidators([Validators.required, Validators.maxLength(4000)]);
        this.contactForm.updateValueAndValidity({ emitEvent: false });
        this.loadRequestTypes();
    }

    private loadRequestTypes(): void {
        // Align with Raise Question flow types, excluding "Other".
        this._translateService.get([...QUESTION_TYPE_KEYS]).subscribe((t) => {
            this.requestTypes = QUESTION_TYPE_KEYS.map((key, i) => ({
                value: i,
                label: t[key] && t[key] !== key ? t[key] : QUESTION_TYPE_FALLBACKS[i],
            }));
        });
    }

    public onHide(): void {
        this._visible = false;
        this.visibleChange.emit(false);
        this.contactForm.reset();
    }

    public onVisibleChange(value: boolean): void {
        this._visible = value;
        this.visibleChange.emit(value);
    }

    public submitContact(): void {
        const id = this.dealId;
        if (id == null || id <= 0) {
            this._helpersService.showErrorMessage('Invalid deal.');
            return;
        }
        if (this.contactForm.invalid) {
            this.contactForm.markAllAsTouched();
            return;
        }

        const formValue = this.contactForm.value;
        const payload = { type: Number(formValue.request), question: String(formValue.comments ?? '').trim() };

        this.submitting.set(true);
        this._dealsApiService
            .raiseDealQuestion(id, payload)
            .pipe(finalize(() => this.submitting.set(false)))
            .subscribe({
                next: () => {
                    this.onHide();
                    this.onSubmitSuccess.emit();
                },
                error: (err) => {
                    this._helpersService.showErrorMessage(err?.message || 'Failed to submit. Please try again.');
                }
            });
    }
}
